// ReportModal.jsx
import React, { useState } from "react";
import { Send, Phone, User } from "lucide-react";
import { useSelector } from "react-redux";
import axios from "axios";
import API_ENDPOINTS from "../service/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { translations } from "../translation/translate";

const ReportModal = ({ isOpen, onClose, reportLink }) => {
  const [selectedOption, setSelectedOption] = useState("registered");
  const [customNumber, setCustomNumber] = useState("");
  const [isSending, setIsSending] = useState(false);
  const langData = useSelector((state) => state.langSlice.langData);
  console.log(reportLink , "report links ")

  const patientDetails = useSelector((state) => state.patientSlice.patientData);
  const UserData = useSelector((state) => state.authSlice.user);

  const registeredNumber = patientDetails?.contactNumber;

  const handleSendReport = async () => {
    // Basic validations
    if (!patientDetails?.PatientID) {
      toast.error("Missing patient details.", { position: "top-center" });
      return;
    }

    if (!Array.isArray(reportLink) || reportLink.length === 0) {
      toast.error("No reports available to send.", { position: "top-center" });
      return;
    }

    // ✅ Validate based on selected option
    if (selectedOption === "registered") {
      if (!registeredNumber) {
        toast.error("No registered contact number found.", {
          position: "top-center",
        });
        return;
      }
      // We trust registered number, no need to send it in payload
    } else if (selectedOption === "custom") {
      if (!customNumber || String(customNumber).trim().length < 8) {
        toast.error("Please enter a valid phone number.", {
          position: "top-center",
        });
        return;
      }
    }

    setIsSending(true);

    try {
      for (let obj of reportLink) {
        // Extract test name (key) and URL (value)
        const testName = Object.keys(obj)[0];   // "CRP"
        const pdfUrl = Object.values(obj)[0];   // "https://..."
      
        if (!pdfUrl) continue;
      
        const postData = {
          PatientID: patientDetails.PatientID,
          reportLink: pdfUrl,
          reportName: testName,                      // ✅ NEW FIELD
          ...(selectedOption === "custom" && {
            contactNumber: String(customNumber).trim(),
          }),
        };
      
        await axios.post(API_ENDPOINTS.sendPdf, postData, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${UserData?.token}`,
          },
        });
      }
      

      toast.success("All reports sent successfully!", {
        position: "top-center",
      });
    } catch (error) {
      console.error("Error sending report:", error);
      toast.error("Failed to send one or more reports", {
        position: "top-center",
      });
    } finally {
      setIsSending(false);
      setCustomNumber("");
      setSelectedOption("registered");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-md rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50 p-6 shadow-lg">
        {isSending && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/80 backdrop-blur">
            <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            <p className="mt-3 text-purple-800 font-semibold">
              {translations[langData].sendingReports}
            </p>
            <p className="text-sm text-purple-600">
              {translations[langData].fewSeconds}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="text-center">
          <h2 className="flex items-center justify-center gap-2 text-2xl font-bold text-purple-800">
            <Send className="h-6 w-6" />
            {translations[langData].sendMedicalReport}
          </h2>
          <p className="mt-2 text-purple-600">
            {translations[langData].chooseSendOption}
          </p>
        </div>

        {/* Options */}
        <div className="space-y-4 py-6">
          {/* Registered number */}
          <label className="flex cursor-pointer items-start space-x-3 rounded-lg border-2 border-purple-200 bg-white/70 p-4 transition-colors hover:bg-purple-50/70">
            <input
              type="radio"
              name="sendOption"
              value="registered"
              checked={selectedOption === "registered"}
              onChange={() => setSelectedOption("registered")}
              className="mt-1 accent-purple-600"
              disabled={isSending}
            />
            <div className="flex-1">
              <span className="flex items-center gap-2 font-semibold text-purple-800">
                <User className="h-4 w-4" />
                {translations[langData].sendToRegistered}
              </span>
              <div className="mt-2 flex items-center gap-2 text-purple-600">
                <Phone className="h-4 w-4" />
                <span className="rounded bg-purple-100 px-2 py-1 font-mono">
                  {registeredNumber || translations[langData].notAvailable}
                </span>
              </div>
            </div>
          </label>

          {/* Custom number */}
          <label className="flex cursor-pointer items-start space-x-3 rounded-lg border-2 border-purple-200 bg-white/70 p-4 transition-colors hover:bg-purple-50/70">
            <input
              type="radio"
              name="sendOption"
              value="custom"
              checked={selectedOption === "custom"}
              onChange={() => setSelectedOption("custom")}
              className="mt-1 accent-purple-600"
              disabled={isSending}
            />
            <div className="flex-1">
              <span className="flex items-center gap-2 font-semibold text-purple-800">
                <Phone className="h-4 w-4" />
                {translations[langData].sendToAnother}
              </span>
              <p className="mt-1 text-sm text-purple-600">
                {translations[langData].enterCustomNumber}
              </p>
            </div>
          </label>
        </div>

        {/* Custom number input */}
        {selectedOption === "custom" && (
          <div className="animate-in space-y-2 duration-300">
            <label
              htmlFor="customNumber"
              className="font-medium text-purple-800"
            >
              {translations[langData].phoneNumber}
            </label>
            <input
              id="customNumber"
              type="tel"
              placeholder={translations[langData].enterPhoneNumber}
              value={customNumber}
              onChange={(e) => setCustomNumber(e.target.value)}
              className="w-full rounded border border-purple-300 px-3 py-2 outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              disabled={isSending}
            />
          </div>
        )}

        {/* Actions */}
        <div className="pt-6">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`flex-1 rounded border border-purple-300 px-4 py-2 text-purple-700 transition-colors hover:bg-purple-50 ${
                isSending ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isSending}
            >
              {translations[langData].cancel}
            </button>

            <button
              onClick={handleSendReport}
              className={`flex flex-1 items-center justify-center gap-2 rounded bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-2 font-semibold text-white shadow-lg transition-all duration-200 hover:from-purple-700 hover:to-blue-700 hover:shadow-xl ${
                isSending ? "opacity-70 cursor-wait" : ""
              }`}
              disabled={isSending}
            >
              {isSending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin" />
                  {translations[langData].sending}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {translations[langData].sendReport}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;